import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Image, Video, Edit2, X, Save, ChevronUp, ChevronDown, Upload } from 'lucide-react';
import FileUploader from './FileUploader';
import { getImageUrl } from '../../lib/utils';

const StoryManager = () => {
  const [stories, setStories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStory, setSelectedStory] = useState(null);
  const [showAddSlide, setShowAddSlide] = useState(false);
  const [newSlide, setNewSlide] = useState({ type: 'image', url: '', caption: '' });
  const [showCreateStory, setShowCreateStory] = useState(false);
  const [newStory, setNewStory] = useState({
    title: '',
    thumbnail: '',
    story_type: 'product',
    product_id: null,
    link_url: ''
  });
  const [uploadMode, setUploadMode] = useState('url'); // 'url' or 'file'

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchStories();
    fetchProducts();
  }, []);

  const fetchStories = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/admin/stories/all`);
      if (res.ok) {
        const data = await res.json();
        setStories(data);
      }
    } catch (error) {
      console.error('Error fetching stories:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/products`);
      if (res.ok) {
        const data = await res.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleCreateStory = async () => {
    try {
      const storyData = {
        ...newStory,
        slides: [],
        order: stories.length
      };
      
      const res = await fetch(`${backendUrl}/api/admin/stories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(storyData)
      });
      
      if (res.ok) {
        await fetchStories();
        setShowCreateStory(false);
        setNewStory({ title: '', thumbnail: '', story_type: 'product', product_id: null, link_url: '' });
      }
    } catch (error) {
      console.error('Error creating story:', error);
    }
  };

  const handleDeleteStory = async (storyId) => {
    if (!window.confirm('Bu hikayeyi silmek istediğinizden emin misiniz?')) return;
    
    try {
      const res = await fetch(`${backendUrl}/api/admin/stories/${storyId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        setStories(stories.filter(s => s.story_id !== storyId));
        if (selectedStory?.story_id === storyId) {
          setSelectedStory(null);
        }
      }
    } catch (error) {
      console.error('Error deleting story:', error);
    }
  };

  const handleAddSlide = async () => {
    if (!selectedStory || !newSlide.url) return;
    
    try {
      const res = await fetch(`${backendUrl}/api/admin/stories/${selectedStory.story_id}/slides`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSlide)
      });
      
      if (res.ok) {
        await fetchStories();
        // Update selected story
        const updatedStory = stories.find(s => s.story_id === selectedStory.story_id);
        if (updatedStory) {
          setSelectedStory({ ...updatedStory, slides: [...(updatedStory.slides || []), newSlide] });
        }
        setShowAddSlide(false);
        setNewSlide({ type: 'image', url: '', caption: '' });
        // Refresh to get updated story
        setTimeout(fetchStories, 500);
      }
    } catch (error) {
      console.error('Error adding slide:', error);
    }
  };

  const handleDeleteSlide = async (storyId, slideIndex) => {
    if (!window.confirm('Bu slide\'ı silmek istediğinizden emin misiniz?')) return;
    
    try {
      const res = await fetch(`${backendUrl}/api/admin/stories/${storyId}/slides/${slideIndex}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        await fetchStories();
        // Update selected story
        if (selectedStory?.story_id === storyId) {
          const newSlides = [...selectedStory.slides];
          newSlides.splice(slideIndex, 1);
          setSelectedStory({ ...selectedStory, slides: newSlides });
        }
      }
    } catch (error) {
      console.error('Error deleting slide:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Hikaye Yönetimi</h2>
        <button
          onClick={() => setShowCreateStory(true)}
          className="flex items-center gap-2 bg-black text-white px-4 py-2 text-sm hover:bg-gray-800"
        >
          <Plus size={16} />
          Yeni Hikaye
        </button>
      </div>

      {/* Create Story Modal */}
      {showCreateStory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Yeni Hikaye Oluştur</h3>
              <button onClick={() => setShowCreateStory(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Başlık</label>
                <input
                  type="text"
                  value={newStory.title}
                  onChange={(e) => setNewStory({ ...newStory, title: e.target.value })}
                  className="w-full border px-3 py-2 text-sm"
                  placeholder="Hikaye başlığı"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Thumbnail URL</label>
                <input
                  type="text"
                  value={newStory.thumbnail}
                  onChange={(e) => setNewStory({ ...newStory, thumbnail: e.target.value })}
                  className="w-full border px-3 py-2 text-sm"
                  placeholder="https://..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Hikaye Tipi</label>
                <select
                  value={newStory.story_type}
                  onChange={(e) => setNewStory({ ...newStory, story_type: e.target.value })}
                  className="w-full border px-3 py-2 text-sm"
                >
                  <option value="product">Ürün Hikayesi</option>
                  <option value="brand">Marka Hikayesi</option>
                </select>
              </div>
              
              {newStory.story_type === 'product' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Ürün</label>
                  <select
                    value={newStory.product_id || ''}
                    onChange={(e) => setNewStory({ ...newStory, product_id: parseInt(e.target.value) || null })}
                    className="w-full border px-3 py-2 text-sm"
                  >
                    <option value="">Ürün Seçin</option>
                    {products.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {newStory.story_type === 'brand' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Link URL (Opsiyonel)</label>
                  <input
                    type="text"
                    value={newStory.link_url}
                    onChange={(e) => setNewStory({ ...newStory, link_url: e.target.value })}
                    className="w-full border px-3 py-2 text-sm"
                    placeholder="/hakkimizda"
                  />
                </div>
              )}
              
              <button
                onClick={handleCreateStory}
                disabled={!newStory.title || !newStory.thumbnail}
                className="w-full bg-black text-white py-2 text-sm hover:bg-gray-800 disabled:bg-gray-300"
              >
                Oluştur
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stories Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Stories List */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-medium mb-4">Hikayeler ({stories.length})</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {stories.map((story) => (
              <div
                key={story.story_id}
                onClick={() => setSelectedStory(story)}
                className={`flex items-center gap-3 p-3 border rounded cursor-pointer transition-colors ${
                  selectedStory?.story_id === story.story_id ? 'border-black bg-gray-50' : 'hover:bg-gray-50'
                }`}
              >
                <img
                  src={getImageUrl(story.thumbnail || story.image_url)}
                  alt={story.title}
                  className="w-12 h-12 rounded-full object-cover bg-gray-100"
                  onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/48'; }}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{story.title}</p>
                  <p className="text-xs text-gray-500">
                    {story.story_type === 'brand' ? 'Marka' : 'Ürün'} • {(story.slides || []).length} slide
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteStory(story.story_id);
                  }}
                  className="p-1 text-red-500 hover:bg-red-50 rounded"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            
            {stories.length === 0 && (
              <p className="text-center text-gray-500 py-8">
                Henüz hikaye yok. Yeni hikaye oluşturun.
              </p>
            )}
          </div>
        </div>

        {/* Selected Story Details */}
        <div className="bg-white border rounded-lg p-4">
          {selectedStory ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium">{selectedStory.title} - Slide'lar</h3>
                <button
                  onClick={() => setShowAddSlide(true)}
                  className="flex items-center gap-1 text-sm bg-black text-white px-3 py-1 hover:bg-gray-800"
                >
                  <Plus size={14} />
                  Slide Ekle
                </button>
              </div>
              
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {(selectedStory.slides || []).map((slide, index) => (
                  <div key={index} className="flex items-center gap-3 p-2 border rounded">
                    <div className="w-16 h-16 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                      {slide.type === 'video' ? (
                        <div className="w-full h-full flex items-center justify-center bg-gray-200">
                          <Video size={20} className="text-gray-500" />
                        </div>
                      ) : (
                        <img
                          src={getImageUrl(slide.image || slide.url)}
                          alt={slide.caption}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 uppercase">{slide.type}</p>
                      <p className="text-sm truncate">{slide.caption || 'Açıklama yok'}</p>
                    </div>
                    <button
                      onClick={() => handleDeleteSlide(selectedStory.story_id, index)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
                
                {(selectedStory.slides || []).length === 0 && (
                  <p className="text-center text-gray-500 py-8">
                    Bu hikayede slide yok. Slide ekleyin.
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              Bir hikaye seçin
            </div>
          )}
        </div>
      </div>

      {/* Add Slide Modal */}
      {showAddSlide && selectedStory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Slide Ekle - {selectedStory.title}</h3>
              <button onClick={() => { setShowAddSlide(false); setUploadMode('url'); }}>
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Tip</label>
                <select
                  value={newSlide.type}
                  onChange={(e) => setNewSlide({ ...newSlide, type: e.target.value })}
                  className="w-full border px-3 py-2 text-sm"
                >
                  <option value="image">Görsel</option>
                  <option value="video">Video</option>
                </select>
              </div>
              
              {/* Upload Mode Toggle */}
              <div className="flex gap-2">
                <button
                  onClick={() => setUploadMode('url')}
                  className={`flex-1 py-2 text-sm border ${uploadMode === 'url' ? 'bg-black text-white' : 'hover:bg-gray-50'}`}
                >
                  URL ile
                </button>
                <button
                  onClick={() => setUploadMode('file')}
                  className={`flex-1 py-2 text-sm border ${uploadMode === 'file' ? 'bg-black text-white' : 'hover:bg-gray-50'}`}
                >
                  Dosya Yükle
                </button>
              </div>
              
              {uploadMode === 'url' ? (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {newSlide.type === 'image' ? 'Görsel URL' : 'Video URL'}
                  </label>
                  <input
                    type="text"
                    value={newSlide.url}
                    onChange={(e) => setNewSlide({ ...newSlide, url: e.target.value })}
                    className="w-full border px-3 py-2 text-sm"
                    placeholder="https://..."
                  />
                </div>
              ) : (
                <FileUploader
                  accept={newSlide.type === 'image' ? 'image/*' : 'video/*'}
                  label={newSlide.type === 'image' ? 'Görsel Seç' : 'Video Seç'}
                  onUpload={(url) => setNewSlide({ ...newSlide, url })}
                />
              )}
              
              <div>
                <label className="block text-sm font-medium mb-1">Açıklama</label>
                <input
                  type="text"
                  value={newSlide.caption}
                  onChange={(e) => setNewSlide({ ...newSlide, caption: e.target.value })}
                  className="w-full border px-3 py-2 text-sm"
                  placeholder="Slide açıklaması"
                />
              </div>
              
              {newSlide.url && newSlide.type === 'image' && (
                <div className="border rounded p-2">
                  <p className="text-xs text-gray-500 mb-2">Önizleme:</p>
                  <img src={newSlide.url} alt="Preview" className="w-full h-32 object-contain" />
                </div>
              )}
              
              <button
                onClick={handleAddSlide}
                disabled={!newSlide.url}
                className="w-full bg-black text-white py-2 text-sm hover:bg-gray-800 disabled:bg-gray-300"
              >
                Ekle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoryManager;
